import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { cn } from '@saas-pages/shared/ui'
import { Seo } from '@saas-pages/shared/public/seo'
import branding from '../../../branding.config'
import { seoFor } from '@/seo/routes'
import { LocalizedProcessingBadge, format, useT } from '@/shared/i18n'
import {
  CATEGORY_ACCENT,
  buildCategoryLabel,
  buildTools,
  type ToolCategory,
  type ToolDefinition,
} from '@/shared/tools/tools'

type CategoryFilter = 'all' | ToolCategory

const CATEGORY_FILTERS: readonly CategoryFilter[] = [
  'all',
  'format',
  'encode',
  'generate',
  'test',
]

export function IndexPage() {
  const t = useT()
  const navigate = useNavigate()
  const tools = buildTools(t).filter((tool) => tool.status === 'available')
  const categoryLabel = buildCategoryLabel(t)
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState<CategoryFilter>('all')
  const normalizedQuery = query.trim().toLocaleLowerCase()
  const filteredTools = tools.filter((tool) => {
    if (category !== 'all' && tool.category !== category) {
      return false
    }

    if (!normalizedQuery) {
      return true
    }

    return [tool.title, tool.shortDescription, ...tool.useCases]
      .join(' ')
      .toLocaleLowerCase()
      .includes(normalizedQuery)
  })
  const firstResult = filteredTools[0]

  return (
    <>
      <Seo {...seoFor('index', t)} ogImage={branding.ogImage} />

      <div className="mx-auto max-w-6xl">
        <section className="max-w-3xl">
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-4xl">
            {t.hero.title}
          </h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground sm:text-base">
            {t.hero.description}
          </p>
        </section>

        <section id="tools" className="mt-5 scroll-mt-4 sm:mt-6">
          <h2 className="sr-only">{t.catalog.title}</h2>

          <label htmlFor="tool-search" className="sr-only">
            {t.catalog.searchLabel}
          </label>
          <div className="relative">
            <input
              id="tool-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && firstResult) {
                  event.preventDefault()
                  void navigate({ to: firstResult.path })
                }
              }}
              placeholder={t.catalog.searchPlaceholder}
              className="h-11 w-full rounded-md border bg-background px-3 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring sm:pr-32"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground sm:block">
              {t.catalog.enterHint}
            </span>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {CATEGORY_FILTERS.map((filter) => {
              const label = filter === 'all' ? t.catalog.allJobs : categoryLabel[filter]
              const count =
                filter === 'all'
                  ? tools.length
                  : tools.filter((tool) => tool.category === filter).length

              return (
                <button
                  key={filter}
                  type="button"
                  aria-pressed={category === filter}
                  onClick={() => setCategory(filter)}
                  className={cn(
                    'flex items-center gap-2 rounded-md px-3 py-2 text-sm outline-none transition-colors',
                    'focus-visible:ring-2 focus-visible:ring-ring',
                    category === filter
                      ? 'bg-foreground text-background'
                      : 'border bg-background text-muted-foreground hover:text-foreground',
                  )}
                >
                  <span>{label}</span>
                  <span className="text-xs opacity-70">{count}</span>
                </button>
              )
            })}
          </div>

          <Link
            to="/json-formatter"
            className="mt-3 flex min-w-0 items-center gap-3 rounded-md border border-primary/30 bg-primary/10 p-3 outline-none transition-colors hover:bg-primary/15 focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded border bg-background font-mono text-xs font-bold text-primary">
              {'{}'}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-xs font-medium text-primary">
                {t.catalog.frequent}
              </span>
              <span className="block text-sm font-semibold">
                {tools.find((tool) => tool.key === 'jsonFormatter')?.title}
              </span>
            </span>
          </Link>

          <div
            className="mt-3 flex items-center justify-between gap-2 border-b pb-2"
            aria-live="polite"
          >
            <span className="text-xs font-medium text-muted-foreground">
              {format(t.catalog.resultCount, { count: filteredTools.length })}
            </span>
            {(query || category !== 'all') && (
              <button
                type="button"
                className="text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => {
                  setQuery('')
                  setCategory('all')
                }}
              >
                {t.catalog.reset}
              </button>
            )}
          </div>

          {filteredTools.length > 0 ? (
            <div className="mt-1 grid min-w-0 gap-1 md:grid-cols-2 lg:grid-cols-3" data-testid="tool-results">
              {filteredTools.map((tool) => (
                <ToolResult
                  key={tool.key}
                  categoryLabel={categoryLabel[tool.category]}
                  tool={tool}
                />
              ))}
            </div>
          ) : (
            <div className="mt-3 rounded-md border border-dashed bg-muted/20 p-6 text-center" role="status">
              <div className="text-sm font-semibold">{t.catalog.noMatchTitle}</div>
              <p className="mt-1 text-sm text-muted-foreground">{t.catalog.noMatchDescription}</p>
            </div>
          )}
        </section>
      </div>
    </>
  )
}

interface ToolResultProps {
  categoryLabel: string
  tool: ToolDefinition
}

function ToolResult({ categoryLabel, tool }: ToolResultProps) {
  return (
    <Link
      to={tool.path}
      className="flex min-w-0 items-start gap-3 rounded-md border border-transparent p-2 text-left outline-none transition-colors hover:border-primary/25 hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded border bg-background font-mono text-[11px] font-bold">
        {tool.icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-semibold">{tool.title}</span>
          <LocalizedProcessingBadge variant={tool.processing} />
          <span
            className={cn(
              'rounded border px-1.5 py-0.5 text-[10px]',
              CATEGORY_ACCENT[tool.category],
            )}
          >
            {categoryLabel}
          </span>
        </span>
        <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
          {tool.shortDescription}
        </span>
      </span>
    </Link>
  )
}
